/**
 * DraftChangeMarks — zeigt im eingebetteten Editor, was sich an einem
 * Entwurf seit der zuletzt gesehenen Fassung geändert hat (#2574, Post 5623).
 *
 * Das Portal öffnet `/frame/post/<id>?seit=<doc_version>`. Hier kommen von
 * `GET /posts/{id}/changes-since` die geänderten und neuen Absätze samt
 * altem Wortlaut. Markiert wird über Attribute an den gerenderten Absätzen
 * (`.read-para[data-block-id]`):
 *   - `data-draft-change` → kurz aufleuchten, danach dezent markiert
 *   - `data-draft-old`    → alter Wortlaut, per CSS durchgestrichen davor
 * Die Absätze rendert React; die Attribute setzt der Effekt nach jedem
 * Rendern neu, weil React sie nicht kennt und beim Neuaufbau verwirft.
 */

import { useEffect, useState } from 'react'
import { CONTENT_API } from '../config'
import { authFetch } from '../api/authFetch'

interface ChangedBlock {
  block_id: string
  status: 'changed' | 'added'
  old_text: string | null
}

interface ChangesResponse {
  from_version: number
  to_version: number
  blocks: ChangedBlock[]
  removed_tail: string[]
}

interface Props {
  postId: number
  sinceVersion: number
  root: HTMLElement | null
  /** Ändert sich mit jedem neuen Stand des Dokuments; löst Nachladen aus. */
  docKey: unknown
}

function cssEsc(value: string): string {
  if (typeof CSS !== 'undefined' && CSS.escape) return CSS.escape(value)
  return value.replace(/["\\]/g, '\\$&')
}

export function DraftChangeMarks({ postId, sinceVersion, root, docKey }: Props) {
  const [changes, setChanges] = useState<ChangesResponse | null>(null)

  useEffect(() => {
    let alive = true
    const timer = window.setTimeout(async () => {
      try {
        const r = await authFetch(
          `${CONTENT_API}/api/v1/posts/${postId}/changes-since?doc_version=${sinceVersion}`,
        )
        if (!r.ok) return // ohne Verlauf: nur ohne Hervorhebung
        const data = (await r.json()) as ChangesResponse
        if (alive) setChanges(data)
      } catch {
        /* still: die Ansicht funktioniert auch ohne Hervorhebung */
      }
    }, 300)
    return () => {
      alive = false
      window.clearTimeout(timer)
    }
  }, [postId, sinceVersion, docKey])

  useEffect(() => {
    if (!root || !changes) return
    const marked: HTMLElement[] = []
    for (const b of changes.blocks) {
      const el = root.querySelector<HTMLElement>(`.read-para[data-block-id="${cssEsc(b.block_id)}"]`)
      if (!el) continue
      el.setAttribute('data-draft-change', b.status)
      if (b.old_text) el.setAttribute('data-draft-old', b.old_text)
      else el.removeAttribute('data-draft-old')
      marked.push(el)
    }
    return () => {
      for (const el of marked) {
        el.removeAttribute('data-draft-change')
        el.removeAttribute('data-draft-old')
      }
    }
  })

  if (!changes || changes.removed_tail.length === 0) return null
  return (
    <div className="draft-removed-tail" aria-label="Entfernt">
      {changes.removed_tail.map((t, i) => (
        <p key={i}>{t}</p>
      ))}
    </div>
  )
}
